import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DOCUMENTS } from '@project/contracts'
import {
  Button, Empty, Field, Icon, Input, MenuButton, Modal, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import {
  useApi,
  type Company,
  type EmailCopyOptions,
  type EmailTemplateView,
  type OutboxMessage,
} from '../app/api.js'
import {
  MAIL_PLACEHOLDERS, mailTemplatesApi, printTemplatesApi,
  type FromMode, type MailTemplateRow, type PrintTemplateDetail, type PrintTemplateRow, type TemplateMode,
} from '../app/templates-api.js'
import { RichTextEditor } from '../framework/RichTextEditor.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { TeamBoard } from '../framework/TeamBoard.js'
import { formatDate, formatDateTime } from '../framework/format.js'
import { CodeEditor, errorLineOf } from '../framework/CodeEditor.js'
import { ColumnsButton, ExportButton, useChosenColumns } from '../framework/columns.js'
import { SavedViews } from '../framework/SavedViews.js'

/**
 * Print and email templates (docs/11 §Data, §Email Templates).
 *
 * The editor is deliberately plain text rather than a WYSIWYG canvas: what is
 * stored IS the template, a rich editor would silently rewrite the expressions
 * inside it, and the people who write these are the same people who wrote the
 * ones on the system being replaced.
 *
 * Preview renders against a real document of that type rather than a fixture,
 * because the mistakes that matter — a missing tax column, a total that reads
 * as `[object Object]` — only appear against real data.
 */

/* ========================= print templates ================================ */

/**
 * Admin › Print Templates (PRN-001…004, PRN-046, SET-040, SET-041).
 *
 * A list of template records, one row each — Name, Document, Applicable for,
 * Companies, Active — with the shipped "Standard" layouts beside the company's
 * own, as the reference keeps them. Several layouts per document type is the
 * point: the Print button on a document offers every active one.
 */
export const PrintTemplates = () => {
  const [open, setOpen] = useState<{ id: string; docType?: string } | null>(null)
  return open
    ? <PrintTemplateEditor id={open.id} initialDocType={open.docType ?? ''} onClose={() => setOpen(null)} onOpen={(id) => setOpen({ id })} />
    : <PrintTemplateList onOpen={(id) => setOpen({ id })} onCreate={(docType) => setOpen({ id: 'new', docType })} />
}

interface TemplateColumn<R = PrintTemplateRow> {
  readonly key: string
  readonly label: string
  readonly render: (row: R) => ReactNode
  readonly width?: number
}

const yesNo = (on: boolean) => <span className={on ? 'badge-standard badge-active' : 'badge-standard badge-inactive'}>{on ? 'Yes' : 'No'}</span>

type AppliesTo = 'ALL' | 'SELECTED'
const APPLIES_OPTIONS: ReadonlyArray<{ value: AppliesTo; label: string }> = [
  { value: 'ALL', label: 'All companies' },
  { value: 'SELECTED', label: 'Only selected companies' },
]

/**
 * "Applicable for" as the reference draws it (SET4-005): two radio pills side
 * by side, so both choices are visible at once rather than hidden in a menu.
 */
const AppliesToPills = ({ value, onChange, disabled }: {
  value: AppliesTo
  onChange: (next: AppliesTo) => void
  disabled: boolean
}) => (
  <div role="radiogroup" aria-label="Applicable for" className="radio-pills">
    {APPLIES_OPTIONS.map((o) => (
      <label key={o.value} className={value === o.value ? 'radio-pill radio-pill--on' : 'radio-pill'}>
        <input type="radio" name="applies-to" value={o.value} checked={value === o.value} disabled={disabled} onChange={() => onChange(o.value)} />
        <span>{o.label}</span>
      </label>
    ))}
  </div>
)

/** A Yes/No switch that is shown even when it cannot be changed, so a locked record still says which way it is. */
const YesNoSwitch = ({ label, on, onChange, disabled }: { label: string; on: boolean; onChange: () => void; disabled: boolean }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} className={on ? 'switch switch--on' : 'switch'} disabled={disabled} onClick={onChange}>
    <span className="switch__knob" />
    <span className="switch__text">{on ? 'Yes' : 'No'}</span>
  </button>
)

const PRINT_COLUMNS: readonly TemplateColumn[] = [
  {
    key: 'name', label: 'Name',
    render: (r) => (
      <span>
        {r.name}
        {r.isDefault ? <span className="pill" style={{ marginLeft: 8 }}>Prints by default</span> : null}
      </span>
    ),
  },
  { key: 'docTypeLabel', label: 'Document', render: (r) => r.docTypeLabel, width: 220 },
  { key: 'appliesTo', label: 'Applicable for', render: (r) => (r.appliesTo === 'ALL' ? 'All companies' : 'Only selected companies'), width: 200 },
  { key: 'companies', label: 'Companies', render: (r) => (r.appliesTo === 'ALL' ? 'All companies' : r.companyNames.join(', ')), width: 200 },
  { key: 'active', label: 'Active', render: (r) => yesNo(r.active), width: 100 },
  { key: 'fileName', label: 'File name', render: (r) => <span className="mono">{r.fileName ?? ''}</span>, width: 170 },
  { key: 'isStandard', label: 'Standard template', render: (r) => (r.isStandard ? 'Yes' : 'No'), width: 150 },
  { key: 'createdAt', label: 'Created on', render: (r) => (r.createdAt ? formatDate(r.createdAt) : ''), width: 130 },
  { key: 'createdByName', label: 'Created by', render: (r) => r.createdByName ?? '', width: 150 },
]
const DEFAULT_PRINT_COLUMNS = ['name', 'docTypeLabel', 'appliesTo', 'companies', 'active']

const DOC_OPTIONS = DOCUMENTS.map((d) => ({ value: d.key, label: d.labelPlural }))
const ACTIVE_OPTIONS = [{ value: '', label: 'Any' }, { value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }]

interface TemplateFilters { docType: string; companyId: string; q: string; active: string }
const NO_FILTERS: TemplateFilters = { docType: '', companyId: '', q: '', active: '' }

/** The filter row both template lists share: Document, Companies, Name, Active. */
export const TemplateFilterBar = ({ filters, onChange, companies }: {
  filters: TemplateFilters
  onChange: (next: TemplateFilters) => void
  companies: readonly Company[]
}) => (
  <div className="template-filters">
    <Field label="Document">
      <Select value={filters.docType} onChange={(docType) => onChange({ ...filters, docType })} options={DOC_OPTIONS} placeholder="Every document" />
    </Field>
    <Field label="Companies">
      <Select value={filters.companyId} onChange={(companyId) => onChange({ ...filters, companyId })}
        options={companies.map((c) => ({ value: c.id, label: c.name }))} placeholder="Every company" />
    </Field>
    <Field label="Name">
      <Input value={filters.q} onChange={(q) => onChange({ ...filters, q })} placeholder="Search by name" />
    </Field>
    <Field label="Active">
      <Select value={filters.active} onChange={(active) => onChange({ ...filters, active })} options={ACTIVE_OPTIONS} />
    </Field>
  </div>
)

const matches = (row: { docType: string; name: string; active: boolean; appliesTo: string; companyIds: readonly string[]; isStandard: boolean }, f: TemplateFilters): boolean =>
  (f.docType === '' || row.docType === f.docType)
  && (f.companyId === '' || row.isStandard || row.appliesTo === 'ALL' || row.companyIds.includes(f.companyId))
  && (f.q.trim() === '' || row.name.toLowerCase().includes(f.q.trim().toLowerCase()))
  && (f.active === '' || (f.active === 'YES') === row.active)

const PrintTemplateList = ({ onOpen, onCreate }: { onOpen: (id: string) => void; onCreate: (docType: string) => void }) => {
  const api = useApi()
  const client = printTemplatesApi(api)
  const rows = useQuery({ queryKey: ['print-templates', api.companyId], queryFn: () => client.rows() })
  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api.companies() })
  const [filters, setFilters] = useState<TemplateFilters>(NO_FILTERS)
  const chooser = useChosenColumns('template:print', PRINT_COLUMNS)
  // The reference opens on five columns; the rest are in the chooser.
  const shown = chooser.chosen.length > 0 ? chooser.visible : PRINT_COLUMNS.filter((c) => DEFAULT_PRINT_COLUMNS.includes(c.key))

  const filtered = useMemo(() => (rows.data ?? []).filter((r) => matches(r, filters)), [rows.data, filters])

  if (rows.isLoading) return <Loading />
  if (rows.error) return <ErrorBlock error={rows.error} />

  const columns: ReadonlyArray<ColumnDef<PrintTemplateRow>> = shown.map((c) => ({
    key: c.key, header: c.label, ...(c.width ? { width: c.width } : {}), render: c.render,
  }))

  return (
    <Panel
      title="All print templates"
      aside={
        <div className="row row--wrap">
          <SavedViews
            scope="template:print"
            filters={{ ...filters }}
            columns={chooser.chosen}
            onApply={(view) => {
              setFilters({ ...NO_FILTERS, ...view.filters })
              if (view.columns.length > 0) chooser.choose(view.columns)
            }}
            onDefault={(view) => {
              setFilters({ ...NO_FILTERS, ...view.filters })
              if (view.columns.length > 0) chooser.choose(view.columns)
            }}
          />
          <ColumnsButton all={PRINT_COLUMNS} state={{ ...chooser, visible: shown }} title="Columns on print templates" />
          <ExportButton href={client.exportUrl(filtered.map((r) => r.id))} disabled={filtered.length === 0} count={filtered.length} what="print templates" />
          <Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={() => onCreate(filters.docType)}>
            Create print template
          </Button>
        </div>
      }
    >
      <TemplateFilterBar filters={filters} onChange={setFilters} companies={companies.data ?? []} />
      <p className="helper num">{filtered.length === 0 ? '0 of 0' : `1–${filtered.length} of ${filtered.length}`}</p>
      <Table
        columns={columns}
        rows={filtered}
        getKey={(r) => r.id}
        onRowClick={(r) => onOpen(r.id)}
        empty={<Empty title="No templates match" body={['Clear a filter to see more.']} />}
      />
    </Panel>
  )
}

interface PrintDraft {
  docType: string
  name: string
  fileName: string
  body: string
  isDefault: boolean
  appliesTo: 'ALL' | 'SELECTED'
  companyIds: string[]
  active: boolean
  mode: TemplateMode
}

const draftOf = (t: PrintTemplateDetail): PrintDraft => ({
  docType: t.docType,
  name: t.name,
  fileName: t.fileName ?? '',
  body: t.body,
  isDefault: t.isDefault,
  appliesTo: t.appliesTo,
  companyIds: [...t.companyIds],
  active: t.active,
  mode: t.mode,
})

const PrintTemplateEditor = ({ id, initialDocType, onClose, onOpen }: {
  id: string
  initialDocType: string
  onClose: () => void
  onOpen: (id: string) => void
}) => {
  const api = useApi()
  const queryClient = useQueryClient()
  const client = printTemplatesApi(api)
  const isNew = id === 'new'
  const existing = useQuery({ queryKey: ['print-template', api.companyId, id], queryFn: () => client.get(id), enabled: !isNew })
  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api.companies() })
  const helpers = useQuery({ queryKey: ['print-helpers'], queryFn: () => api.printTemplateHelpers() })

  const [draft, setDraft] = useState<PrintDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [showReference, setShowReference] = useState(false)

  useEffect(() => {
    if (!isNew && existing.data) setDraft(draftOf(existing.data))
  }, [isNew, existing.data])

  useEffect(() => {
    if (!isNew || draft) return
    setDraft({
      docType: initialDocType, name: '', fileName: '#{data.doc_no}', body: '', isDefault: false,
      appliesTo: 'SELECTED', companyIds: [api.companyId], active: true, mode: 'HANDLEBARS',
    })
  }, [isNew, draft, initialDocType, api.companyId])

  // A new template starts from the type's shipped layout, as soon as the type is chosen.
  const starter = useQuery({
    queryKey: ['print-standard', draft?.docType],
    queryFn: () => client.standard(draft?.docType ?? ''),
    enabled: isNew && Boolean(draft?.docType) && draft?.body === '',
  })
  useEffect(() => {
    if (isNew && draft && draft.body === '' && starter.data) setDraft({ ...draft, body: starter.data.body })
  }, [isNew, draft, starter.data])

  const fields = useQuery({
    queryKey: ['print-fields', api.companyId, draft?.docType],
    queryFn: () => api.printTemplateFields(draft?.docType ?? ''),
    enabled: showReference && Boolean(draft?.docType),
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['print-templates', api.companyId] })
    void queryClient.invalidateQueries({ queryKey: ['print-template', api.companyId] })
    void queryClient.invalidateQueries({ queryKey: ['layouts', api.companyId] })
  }
  const failed = (err: unknown) => { setNotice(null); setError(messageFor(err)) }

  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Nothing to save.')
      // File Name* is required (SET4-004); the server refuses a blank one too.
      if (draft.fileName.trim() === '') throw new Error('Give the template a file name, for example #{data.doc_no}.')
      return client.save({
        ...(isNew ? {} : { id }),
        docType: draft.docType, name: draft.name, fileName: draft.fileName,
        body: draft.body, isDefault: draft.isDefault, appliesTo: draft.appliesTo,
        companyIds: draft.appliesTo === 'ALL' ? [] : draft.companyIds, active: draft.active, mode: draft.mode,
      })
    },
    onSuccess: (row) => { setError(null); setNotice('Saved.'); refresh(); if (isNew) onOpen(row.id) },
    onError: failed,
  })
  const copy = useMutation({
    mutationFn: () => client.copy(id),
    onSuccess: (row) => { setError(null); refresh(); onOpen(row.id) },
    onError: failed,
  })
  const toggle = useMutation({
    mutationFn: (active: boolean) => client.setActive(id, active),
    onSuccess: (row) => { setError(null); setNotice(row.active ? 'Activated.' : 'Deactivated.'); refresh() },
    onError: failed,
  })
  const makeDefault = useMutation({
    mutationFn: () => client.makeDefault(id),
    onSuccess: () => { setError(null); setNotice('This document type now prints with this layout.'); refresh() },
    onError: failed,
  })
  const remove = useMutation({
    mutationFn: () => client.remove(id),
    onSuccess: () => { refresh(); onClose() },
    onError: failed,
  })
  const render = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Nothing to preview.')
      return client.preview(draft.docType, draft.body, draft.mode)
    },
    onSuccess: (html) => { setError(null); setPreview(html) },
    onError: failed,
  })

  if (!isNew && existing.isLoading) return <Loading />
  if (existing.error) return <ErrorBlock error={existing.error} />
  if (!draft) return <Loading />

  const record = existing.data
  const standard = record?.isStandard === true
  const locked = standard || (record ? !record.editable : false)
  const active = record ? record.active : true

  const menu = [
    ...(!isNew ? [{ key: 'copy', label: 'Copy', onSelect: () => copy.mutate() }] : []),
    ...(!isNew && record && !record.isDefault && active && (standard || record.editable)
      ? [{ key: 'default', label: 'Print this document with this layout', onSelect: () => makeDefault.mutate() }] : []),
    ...(!isNew && (standard || record?.editable)
      ? [{ key: 'active', label: active ? 'Deactivate' : 'Activate', onSelect: () => toggle.mutate(!active) }] : []),
    ...(!isNew && !standard && record?.editable
      ? [{ key: 'delete', label: 'Delete', danger: true, onSelect: () => remove.mutate() }] : []),
  ]

  return (
    <>
      <Panel
        title={isNew ? 'Create print template' : draft.name}
        aside={
          <div className="row row--wrap">
            {!isNew ? <span className={active ? 'badge-standard badge-active' : 'badge-standard badge-inactive'}>{active ? 'Active' : 'Inactive'}</span> : null}
            <Button kind="ghost" onClick={() => setShowReference((on) => !on)}>{showReference ? 'Hide reference' : 'What can I write?'}</Button>
            <Button kind="ghost" disabled={render.isPending || !draft.docType} onClick={() => render.mutate()}>Preview</Button>
            {/* Close is always there, a shipped read-only layout included (SET3-002). */}
            <Button kind="ghost" onClick={onClose}>Close</Button>
            {menu.length > 0 ? <MenuButton label="Action" items={menu} /> : null}
            {!locked ? <Button kind="primary" disabled={save.isPending || !draft.docType} onClick={() => save.mutate()}>{isNew ? 'Create' : 'Save'}</Button> : null}
          </div>
        }
      >
        {error ? <Notice kind="bad">{error}</Notice> : null}
        {notice && !error ? <Notice kind="ok">{notice}</Notice> : null}
        {standard ? (
          <p className="helper">A shipped layout is read-only. Copy it to make your own, or deactivate it so your own prints instead.</p>
        ) : null}

        {/* General Info in the reference's two-column order (SET2-006, SET3-001):
            Document | Name; Applicable for | File Name; PUG Template. */}
        <h3 className="subhead row">General Info{standard ? <span className="badge-standard">Standard</span> : null}</h3>
        <div className="form-grid">
          <Field label="Document" required>
            {isNew
              ? <Select value={draft.docType} onChange={(docType) => setDraft({ ...draft, docType, body: '' })} options={DOC_OPTIONS} placeholder="Choose a document" />
              : <Input value={record?.docTypeLabel ?? draft.docType} onChange={() => undefined} disabled />}
          </Field>
          <Field label="Name" required>
            <Input value={draft.name} disabled={locked} onChange={(name) => setDraft({ ...draft, name })} />
          </Field>
          <Field label="Applicable for" required>
            <AppliesToPills value={draft.appliesTo} disabled={locked} onChange={(appliesTo) => setDraft({ ...draft, appliesTo })} />
          </Field>
          <Field label="File Name" required hint="Names the PDF. #{data.doc_no} and {{doc_no}} both work.">
            <Input value={draft.fileName} disabled={locked} onChange={(fileName) => setDraft({ ...draft, fileName })} />
          </Field>
          <Field label="PUG Template" hint="Written in PUG's indented syntax rather than HTML.">
            <YesNoSwitch label="PUG Template" on={draft.mode === 'PUG'} disabled={locked}
              onChange={() => setDraft({ ...draft, mode: draft.mode === 'PUG' ? 'HANDLEBARS' : 'PUG' })} />
          </Field>
        </div>
        {draft.appliesTo === 'SELECTED' && !standard ? (
          <div className="company-picks" role="group" aria-label="Companies">
            {(companies.data ?? []).map((c) => (
              <label key={c.id} className="column-choice">
                <input
                  type="checkbox"
                  disabled={locked}
                  checked={draft.companyIds.includes(c.id)}
                  onChange={() => setDraft({
                    ...draft,
                    companyIds: draft.companyIds.includes(c.id) ? draft.companyIds.filter((x) => x !== c.id) : [...draft.companyIds, c.id],
                  })}
                />
                <span>{c.name}</span>
              </label>
            ))}
          </div>
        ) : null}
        {isNew ? (
          <div className="row row--wrap" style={{ marginTop: 12 }}>
            <label className="column-choice">
              <input type="checkbox" checked={draft.isDefault} onChange={() => setDraft({ ...draft, isDefault: !draft.isDefault })} />
              <span>Print this document with this layout</span>
            </label>
          </div>
        ) : null}

        <h3 className="subhead" style={{ marginTop: 20 }}>Print content — {draft.mode === 'PUG' ? 'PUG' : 'HTML'} code</h3>
        <CodeEditor
          value={draft.body}
          onChange={(body) => setDraft({ ...draft, body })}
          mode={draft.mode}
          readOnly={locked}
          errorLine={errorLineOf(error)}
          label="Template content"
        />

        {showReference ? (
          <div className="two-col" style={{ marginTop: 16 }}>
            <div>
              <h3 className="subhead">Helpers</h3>
              <table className="tbl tbl--compact">
                <tbody>
                  {(helpers.data ?? []).map((h) => (
                    <tr key={h.name}><td className="mono">{h.usage}</td><td>{h.means}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="subhead">Values on this document</h3>
              {fields.isLoading ? <Loading what="Reading a document…" /> : null}
              <ul className="plain-list mono">
                {(fields.data ?? []).map((name) => <li key={name}>{`{{${name}}}`}</li>)}
              </ul>
            </div>
          </div>
        ) : null}
      </Panel>

      {/* Notes and files on a saved layout, and who changed it (SET-034). */}
      {!isNew && record ? <TeamBoard record={{ type: 'print_template', id: record.id }} /> : null}

      {preview !== null ? (
        <Modal
          title="Preview"
          subtitle="Rendered against the most recent document of this type."
          wide
          actions={<Button kind="ghost" onClick={() => setPreview(null)}>Close</Button>}
          onClose={() => setPreview(null)}
        >
          {/* Sandboxed: a template is administrator input, and it renders here
              with no access to the session it was previewed from. */}
          <iframe
            title="Template preview"
            sandbox=""
            srcDoc={preview}
            style={{ width: '100%', height: '60vh', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', background: '#fff' }}
          />
        </Modal>
      ) : null}
    </>
  )
}

/* ========================= email templates ================================ */

/**
 * Admin › Email Templates (PRN-047, PRN-048): document email templates as
 * records — several per document, Active or not, for all companies or the
 * ones chosen — with the shipped wordings as read-only Standard rows. The
 * messages the system sends about itself (approvals, exports) are kept below
 * as they were: one wording per event.
 */
export const EmailTemplates = () => {
  const [open, setOpen] = useState<{ id: string; docType?: string } | null>(null)
  return open
    ? <MailTemplateEditor id={open.id} initialDocType={open.docType ?? ''} onClose={() => setOpen(null)} onOpen={(id) => setOpen({ id })} />
    : (
      <>
        <MailTemplateList onOpen={(id) => setOpen({ id })} onCreate={(docType) => setOpen({ id: 'new', docType })} />
        <NotificationTemplates />
      </>
    )
}

const MAILABLE = DOCUMENTS.filter((d) => (d.actions as readonly string[]).includes('email'))
const MAIL_DOC_OPTIONS = MAILABLE.map((d) => ({ value: d.key, label: d.labelPlural }))

/**
 * The columns an email template list can show (SET4-002): the five the
 * reference opens on, then Standard template, Created on and Created by in the
 * chooser and in Create View, as on the print list.
 */
const MAIL_COLUMNS: ReadonlyArray<TemplateColumn<MailTemplateRow>> = [
  {
    key: 'name', label: 'Name',
    render: (r) => <span>{r.name}{r.isDefault ? <span className="pill" style={{ marginLeft: 8 }}>Sent by default</span> : null}</span>,
  },
  { key: 'docTypeLabel', label: 'Document', render: (r) => r.docTypeLabel, width: 220 },
  { key: 'appliesTo', label: 'Applicable for', render: (r) => (r.appliesTo === 'ALL' ? 'All companies' : 'Only selected companies'), width: 200 },
  { key: 'companies', label: 'Companies', render: (r) => (r.appliesTo === 'ALL' ? 'All companies' : r.companyNames.join(', ')), width: 200 },
  { key: 'active', label: 'Active', render: (r) => yesNo(r.active), width: 100 },
  { key: 'isStandard', label: 'Standard template', render: (r) => (r.isStandard ? 'Yes' : 'No'), width: 150 },
  { key: 'createdAt', label: 'Created on', render: (r) => (r.createdAt ? formatDate(r.createdAt) : ''), width: 130 },
  { key: 'createdByName', label: 'Created by', render: (r) => r.createdByName ?? '', width: 150 },
]
const DEFAULT_MAIL_COLUMNS = ['name', 'docTypeLabel', 'appliesTo', 'companies', 'active']

const MailTemplateList = ({ onOpen, onCreate }: { onOpen: (id: string) => void; onCreate: (docType: string) => void }) => {
  const api = useApi()
  const client = mailTemplatesApi(api)
  const rows = useQuery({ queryKey: ['mail-records', api.companyId], queryFn: () => client.rows() })
  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api.companies() })
  const [filters, setFilters] = useState<TemplateFilters>(NO_FILTERS)
  const chooser = useChosenColumns('template:email', MAIL_COLUMNS)
  const shown = chooser.chosen.length > 0 ? chooser.visible : MAIL_COLUMNS.filter((c) => DEFAULT_MAIL_COLUMNS.includes(c.key))
  const filtered = useMemo(() => (rows.data ?? []).filter((r) => matches(r, filters)), [rows.data, filters])

  if (rows.isLoading) return <Loading />
  if (rows.error) return <ErrorBlock error={rows.error} />

  const columns: ReadonlyArray<ColumnDef<MailTemplateRow>> = shown.map((c) => ({
    key: c.key, header: c.label, ...(c.width ? { width: c.width } : {}), render: c.render,
  }))
  const applyView = (view: { filters: Record<string, string>; columns: readonly string[] }) => {
    setFilters({ ...NO_FILTERS, ...view.filters })
    if (view.columns.length > 0) chooser.choose(view.columns)
  }

  return (
    <Panel
      title="All email templates"
      aside={
        <div className="row row--wrap">
          <SavedViews scope="template:email" filters={{ ...filters }} columns={chooser.chosen} onApply={applyView} onDefault={applyView} />
          <ColumnsButton all={MAIL_COLUMNS} state={{ ...chooser, visible: shown }} title="Columns on email templates" />
          <ExportButton href={client.exportUrl(filtered.map((r) => r.id))} disabled={filtered.length === 0} count={filtered.length} what="email templates" />
          <Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={() => onCreate(filters.docType)}>Create email template</Button>
        </div>
      }
    >
      <TemplateFilterBar filters={filters} onChange={setFilters} companies={companies.data ?? []} />
      <p className="helper num">{filtered.length === 0 ? '0 of 0' : `1–${filtered.length} of ${filtered.length}`}</p>
      <Table columns={columns} rows={filtered} getKey={(r) => r.id} onRowClick={(r) => onOpen(r.id)}
        empty={<Empty title="No templates match" body={['Clear a filter to see more.']} />} />
    </Panel>
  )
}

/** Plain shipped wording, shown in the HTML editor without losing its paragraphs. */
const asHtml = (r: MailTemplateRow): string => (r.bodyIsHtml
  ? r.body
  : r.body.split(/\n{2,}/).map((p) => `<p>${p.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string)).replace(/\n/g, '<br>')}</p>`).join(''))

interface MailDraft {
  docType: string; name: string; appliesTo: 'ALL' | 'SELECTED'; companyIds: string[]; fromMode: FromMode
  cc: string; bcc: string; ccProjectRoles: string[]; bccProjectRoles: string[]; senderInBcc: boolean
  attachPrint: boolean; subject: string; body: string; isDefault: boolean; active: boolean
}

const splitList = (v: string): string[] => v.split(/[,;\s]+/).map((a) => a.trim()).filter(Boolean)

const MailTemplateEditor = ({ id, initialDocType, onClose, onOpen }: {
  id: string; initialDocType: string; onClose: () => void; onOpen: (id: string) => void
}) => {
  const api = useApi()
  const client = mailTemplatesApi(api)
  const queryClient = useQueryClient()
  const isNew = id === 'new'
  const rows = useQuery({ queryKey: ['mail-records', api.companyId], queryFn: () => client.rows() })
  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api.companies() })
  const roles = useQuery({ queryKey: ['project-roles', api.companyId], queryFn: () => api.masterList('project_role', { pageSize: '200' }) })
  const record = rows.data?.find((r) => r.id === id)
  const [draft, setDraft] = useState<MailDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (draft) return
    if (isNew) {
      setDraft({
        docType: MAILABLE.some((d) => d.key === initialDocType) ? initialDocType : '', name: '', appliesTo: 'SELECTED',
        companyIds: [api.companyId], fromMode: 'SENDER', cc: '', bcc: '', ccProjectRoles: [], bccProjectRoles: [],
        senderInBcc: false, attachPrint: true, subject: '{company__legal_name}: {doc_type} #{doc_no}',
        body: '<p>Dear {party__label},</p><p>Please find attached {doc_type} {doc_no}.</p>', isDefault: false, active: true,
      })
    } else if (record) {
      setDraft({
        docType: record.docType, name: record.name, appliesTo: record.appliesTo, companyIds: [...record.companyIds],
        fromMode: record.fromMode, cc: record.cc.join(', '), bcc: record.bcc.join(', '),
        ccProjectRoles: [...record.ccProjectRoles], bccProjectRoles: [...record.bccProjectRoles], senderInBcc: record.senderInBcc,
        attachPrint: record.attachPrint, subject: record.subject, body: asHtml(record), isDefault: record.isDefault, active: record.active,
      })
    }
  }, [draft, isNew, record, initialDocType, api.companyId])

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['mail-records', api.companyId] })
    void queryClient.invalidateQueries({ queryKey: ['mail-choices', api.companyId] })
  }
  const failed = (err: unknown) => { setNotice(null); setError(messageFor(err)) }
  const after = (message: string) => (row: MailTemplateRow) => { setError(null); setNotice(message); setDraft(null); refresh(); if (row.id !== id) onOpen(row.id) }

  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Nothing to save.')
      return client.save({
        ...(isNew ? {} : { id }), docType: draft.docType, name: draft.name, appliesTo: draft.appliesTo,
        companyIds: draft.appliesTo === 'ALL' ? [] : draft.companyIds, fromMode: draft.fromMode,
        cc: splitList(draft.cc), bcc: splitList(draft.bcc), ccProjectRoles: draft.ccProjectRoles,
        bccProjectRoles: draft.bccProjectRoles, senderInBcc: draft.senderInBcc, attachPrint: draft.attachPrint,
        subject: draft.subject, body: draft.body, isDefault: draft.isDefault, active: draft.active,
      })
    },
    onSuccess: after('Saved.'),
    onError: failed,
  })
  const copy = useMutation({ mutationFn: () => client.copy(id), onSuccess: after('Copied. This is your copy.'), onError: failed })
  const toggle = useMutation({ mutationFn: (on: boolean) => client.setActive(id, on), onSuccess: after('Done.'), onError: failed })
  const makeDefault = useMutation({ mutationFn: () => client.makeDefault(id), onSuccess: after('This document is now sent with this template.'), onError: failed })
  const remove = useMutation({ mutationFn: () => client.remove(id), onSuccess: () => { refresh(); onClose() }, onError: failed })

  if (rows.isLoading) return <Loading />
  if (!isNew && rows.data && !record) return <ErrorBlock error={new Error('That email template is not there any more.')} />
  if (!draft) return <Loading />

  const standard = record?.isStandard === true
  const locked = standard || (record ? !record.editable : false)
  const roleOptions = [{ value: 'ALL', label: 'The whole project team' },
    ...(roles.data?.rows ?? []).map((r) => ({ value: String(r['id']), label: String(r['name'] ?? '') }))]
  const pickRoles = (key: 'ccProjectRoles' | 'bccProjectRoles', label: string) => (
    <Field label={label}>
      <ChipPicker label={label} value={draft[key]} options={roleOptions} disabled={locked}
        onChange={(next) => setDraft({ ...draft, [key]: next })} />
    </Field>
  )

  const menu = [
    ...(!isNew ? [{ key: 'copy', label: 'Copy', onSelect: () => copy.mutate() }] : []),
    ...(!isNew && record && record.active && !record.isDefault && (standard || record.editable) ? [{ key: 'default', label: 'Send this document with this template', onSelect: () => makeDefault.mutate() }] : []),
    ...(!isNew && record && (standard || record.editable) ? [{ key: 'active', label: record.active ? 'Deactivate' : 'Activate', onSelect: () => toggle.mutate(!record.active) }] : []),
    ...(!isNew && !standard && record?.editable ? [{ key: 'delete', label: 'Delete', danger: true, onSelect: () => remove.mutate() }] : []),
  ]

  return (
    <Panel
      title={isNew ? 'Create email template' : draft.name}
      aside={
        <div className="row row--wrap">
          {record ? <span className={record.active ? 'badge-standard badge-active' : 'badge-standard badge-inactive'}>{record.active ? 'Active' : 'Inactive'}</span> : null}
          {/* Close before Action, on a standard template too (SET3-002). */}
          <Button kind="ghost" onClick={onClose}>Close</Button>
          {menu.length > 0 ? <MenuButton label="Action" items={menu} /> : null}
          {!locked ? <Button kind="primary" disabled={save.isPending || !draft.docType} onClick={() => save.mutate()}>{isNew ? 'Create' : 'Save'}</Button> : null}
        </div>
      }
    >
      {error ? <Notice kind="bad">{error}</Notice> : null}
      {notice && !error ? <Notice kind="ok">{notice}</Notice> : null}
      {standard ? <p className="helper">The shipped wording is read-only here; its text is edited under Notifications below the list. Copy it to make a template of your own.</p> : null}

      <h3 className="subhead row">General Info{standard ? <span className="badge-standard">Standard</span> : null}</h3>
      <div className="form-grid">
        <Field label="Document" required>
          {isNew
            ? <Select value={draft.docType} onChange={(docType) => setDraft({ ...draft, docType })} options={MAIL_DOC_OPTIONS} placeholder="Choose a document" />
            : <Input value={record?.docTypeLabel ?? ''} onChange={() => undefined} disabled />}
        </Field>
        <Field label="Name" required><Input value={draft.name} disabled={locked} onChange={(name) => setDraft({ ...draft, name })} /></Field>
        <Field label="Applicable for" required>
          <AppliesToPills value={draft.appliesTo} disabled={locked} onChange={(appliesTo) => setDraft({ ...draft, appliesTo })} />
        </Field>
      </div>
      {draft.appliesTo === 'SELECTED' && !standard ? (
        <div className="company-picks" role="group" aria-label="Companies">
          {(companies.data ?? []).map((c) => (
            <label key={c.id} className="column-choice">
              <input type="checkbox" disabled={locked} checked={draft.companyIds.includes(c.id)}
                onChange={() => setDraft({ ...draft, companyIds: draft.companyIds.includes(c.id) ? draft.companyIds.filter((x) => x !== c.id) : [...draft.companyIds, c.id] })} />
              <span>{c.name}</span>
            </label>
          ))}
        </div>
      ) : null}

      <h3 className="subhead" style={{ marginTop: 20 }}>Email Info</h3>
      {!draft.docType ? <p className="helper">Please select a document.</p> : (
        <>
          {/* The reference pairs each address box with its project-team picker
              (SET4-003): From | Cc; Cc Project Team | Bcc; Bcc Project Team | Add sender in Bcc. */}
          <div className="form-grid">
            <Field label="From" required hint="The message leaves from our address; this is who it is shown as from, and where replies go.">
              <Select value={draft.fromMode} disabled={locked} onChange={(v) => setDraft({ ...draft, fromMode: v === 'COMPANY' ? 'COMPANY' : 'SENDER' })}
                options={[{ value: 'SENDER', label: "Sender's communication email address" }, { value: 'COMPANY', label: "The company's email address" }]} />
            </Field>
            <Field label="Cc" hint="Addresses, separated by commas."><Input value={draft.cc} disabled={locked} onChange={(cc) => setDraft({ ...draft, cc })} /></Field>
            {pickRoles('ccProjectRoles', 'Cc Project Team')}
            <Field label="Bcc" hint="Addresses, separated by commas."><Input value={draft.bcc} disabled={locked} onChange={(bcc) => setDraft({ ...draft, bcc })} /></Field>
            {pickRoles('bccProjectRoles', 'Bcc Project Team')}
            <div className="stack" style={{ gap: 0, alignSelf: 'center' }}>
              <label className="column-choice">
                <input type="checkbox" disabled={locked} checked={draft.senderInBcc} onChange={() => setDraft({ ...draft, senderInBcc: !draft.senderInBcc })} />
                <span>Add sender in Bcc</span>
              </label>
              <label className="column-choice">
                <input type="checkbox" disabled={locked} checked={draft.attachPrint} onChange={() => setDraft({ ...draft, attachPrint: !draft.attachPrint })} />
                <span>Attach the printed document</span>
              </label>
              {isNew ? (
                <label className="column-choice">
                  <input type="checkbox" checked={draft.isDefault} onChange={() => setDraft({ ...draft, isDefault: !draft.isDefault })} />
                  <span>Send this document with this template</span>
                </label>
              ) : null}
            </div>
          </div>
          <Field label="Subject" required><Input value={draft.subject} disabled={locked} onChange={(subject) => setDraft({ ...draft, subject })} /></Field>
          <Field label="Email body" required>
            <RichTextEditor value={draft.body} readOnly={locked} onChange={(body) => setDraft({ ...draft, body })} placeholders={MAIL_PLACEHOLDERS} />
          </Field>
          <p className="helper">Email signature is not required here. The sender's email signature will be added automatically at the end of each email.</p>
        </>
      )}
      {/* Notes and files on the template, and who changed it (SET-034). */}
      {!isNew && record ? <TeamBoard record={{ type: 'mail_template', id: record.id }} /> : null}
    </Panel>
  )
}

/**
 * A multi-select dropdown (SET4-003): the chosen options as chips with ×, and
 * a dropdown of the ones not yet chosen to add another. Used for the Cc / Bcc
 * project-team pickers, whose options include "The whole project team" as
 * well as each project role, so it cannot be a plain master lookup.
 */
const ChipPicker = ({ label, value, options, onChange, disabled }: {
  label: string
  value: readonly string[]
  options: ReadonlyArray<{ value: string; label: string }>
  onChange: (next: string[]) => void
  disabled: boolean
}) => {
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v
  const left = options.filter((o) => !value.includes(o.value))
  return (
    <div className="chip-picker">
      {value.length > 0 ? (
        <div className="chip-picker__chips">
          {value.map((v) => (
            <span key={v} className="chip-picker__chip">
              {labelOf(v)}
              {!disabled ? (
                <button type="button" aria-label={`Remove ${labelOf(v)}`} onClick={() => onChange(value.filter((x) => x !== v))}>×</button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
      {!disabled && left.length > 0 ? (
        <Select value="" aria-label={label} placeholder="Choose…" options={left} onChange={(v) => { if (v) onChange([...value, v]) }} />
      ) : null}
    </div>
  )
}

/** The messages the system sends about itself: one wording per event, as before. */
const NotificationTemplates = () => {
  const api = useApi()
  const client = useQueryClient()
  const templates = useQuery({ queryKey: ['mail-templates', api.companyId], queryFn: () => api.mailTemplates() })
  const status = useQuery({ queryKey: ['mail-status'], queryFn: () => api.mailStatus() })

  const [editing, setEditing] = useState<
    { key: string; subject: string; body: string; copies: EmailCopyOptions } | null
  >(null)
  const [error, setError] = useState<string | null>(null)

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['mail-templates', api.companyId] })
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!editing) throw new Error('Nothing to save.')
      return api.saveMailTemplate(editing.key, editing.subject, editing.body, editing.copies)
    },
    onSuccess: () => { setEditing(null); setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const reset = useMutation({
    mutationFn: (key: string) => api.resetMailTemplate(key),
    onSuccess: () => { setEditing(null); setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  if (templates.isLoading) return <Loading />
  if (templates.error) return <ErrorBlock error={templates.error} />

  const columns: ReadonlyArray<ColumnDef<EmailTemplateView>> = [
    { key: 'label', header: 'Email', width: 240 },
    { key: 'when', header: 'Sent when' },
    { key: 'subject', header: 'Subject', width: 300 },
    {
      key: 'customised',
      header: 'Wording',
      width: 140,
      render: (row) => (row.customised ? 'Yours' : 'As shipped'),
    },
  ]

  return (
    <>
      {status.data && !status.data.transportConfigured ? (
        <Notice kind="warn">
          No mail server is configured, so messages are queued and held rather than sent.
          Set SMTP_URL on the server to release them.
        </Notice>
      ) : null}

      <Panel title="Notifications">
        <p className="helper">
          Do not sign these off yourself — the sender's own signature is appended to every
          message automatically (docs/15 R-12.4).
        </p>
        <Table
          columns={columns}
          rows={templates.data ?? []}
          getKey={(row) => row.key}
          onRowClick={(row) => {
            setError(null)
            setEditing({ key: row.key, subject: row.subject, body: row.body, copies: row.copies })
          }}
          empty={<Empty title="No templates" body={['Nothing to configure yet.']} />}
        />
      </Panel>

      {editing ? (
        <Modal
          title={templates.data?.find((t) => t.key === editing.key)?.label ?? 'Email'}
          subtitle={templates.data?.find((t) => t.key === editing.key)?.when}
          wide
          actions={
            <>
              <Button kind="ghost" onClick={() => reset.mutate(editing.key)} disabled={reset.isPending}>
                Back to the shipped wording
              </Button>
              <Button kind="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button kind="primary" onClick={() => save.mutate()} disabled={save.isPending}>Save</Button>
            </>
          }
          onClose={() => setEditing(null)}
        >
          {error ? <Notice kind="bad">{error}</Notice> : null}
          <p className="helper">
            Placeholders:{' '}
            <span className="mono">
              {(templates.data?.find((t) => t.key === editing.key)?.placeholders ?? []).join(' · ')}
            </span>
          </p>
          <Field label="Subject">
            <Input value={editing.subject} onChange={(subject) => setEditing({ ...editing, subject })} />
          </Field>
          <Field label="Message">
            <textarea
              className="control"
              style={{ minHeight: 280 }}
              value={editing.body}
              onChange={(event) => setEditing({ ...editing, body: event.currentTarget.value })}
            />
          </Field>

          {templates.data?.find((t) => t.key === editing.key)?.outward ? (
            <>
              <h3 className="subhead" style={{ marginTop: 20 }}>Who else gets a copy</h3>
              <p className="helper">
                Copying the team in Cc tells the supplier who is watching their order. Bcc copies
                the same people without saying so.
              </p>
              <div className="column-choices">
                <label className="column-choice">
                  <input
                    type="checkbox"
                    checked={editing.copies.ccProjectTeam}
                    onChange={() => setEditing({
                      ...editing,
                      copies: {
                        ...editing.copies,
                        ccProjectTeam: !editing.copies.ccProjectTeam,
                        // The same people cannot be both told and not told.
                        bccProjectTeam: false,
                      },
                    })}
                  />
                  <span>Cc the project team</span>
                </label>
                <label className="column-choice">
                  <input
                    type="checkbox"
                    checked={editing.copies.bccProjectTeam}
                    onChange={() => setEditing({
                      ...editing,
                      copies: {
                        ...editing.copies,
                        bccProjectTeam: !editing.copies.bccProjectTeam,
                        ccProjectTeam: false,
                      },
                    })}
                  />
                  <span>Bcc the project team</span>
                </label>
                <label className="column-choice">
                  <input
                    type="checkbox"
                    checked={editing.copies.senderInBcc}
                    onChange={() => setEditing({
                      ...editing,
                      copies: { ...editing.copies, senderInBcc: !editing.copies.senderInBcc },
                    })}
                  />
                  <span>Bcc whoever sent it — the message leaves from our address, not theirs</span>
                </label>
              </div>
            </>
          ) : null}
        </Modal>
      ) : null}
    </>
  )
}

/* ============================== outbox ==================================== */

const OUTBOX_FILTERS = [
  { value: '', label: 'Everything' },
  { value: 'QUEUED', label: 'Waiting to go' },
  { value: 'SENT', label: 'Sent' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'HELD', label: 'Held — no mail server' },
  { value: 'TRAINING', label: 'Held — training company' },
]

export const Outbox = () => {
  const api = useApi()
  const client = useQueryClient()
  const [status, setStatus] = useState('')
  const messages = useQuery({
    queryKey: ['outbox', api.companyId, status],
    queryFn: () => api.outbox(status === '' ? undefined : status),
  })
  const drain = useMutation({
    mutationFn: () => api.drainOutbox(),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['outbox', api.companyId] }) },
  })

  const columns: ReadonlyArray<ColumnDef<OutboxMessage>> = [
    { key: 'createdAt', header: 'Queued', width: 160, render: (r) => formatDateTime(r.createdAt) },
    { key: 'to', header: 'To', width: 240, render: (r) => r.toName ?? r.to },
    { key: 'subject', header: 'Subject' },
    { key: 'documentNo', header: 'Document', type: 'docno', width: 170 },
    { key: 'status', header: 'State', width: 130 },
    { key: 'attempts', header: 'Tries', align: 'right', width: 80 },
    { key: 'lastError', header: 'Last problem', render: (r) => r.lastError ?? '—' },
  ]

  if (messages.isLoading) return <Loading />
  if (messages.error) return <ErrorBlock error={messages.error} />

  return (
    <Panel
      title="Outbox"
      aside={
        <div className="row">
          <Select value={status} onChange={setStatus} options={OUTBOX_FILTERS} />
          <Button kind="ghost" disabled={drain.isPending} onClick={() => drain.mutate()}>
            Try the queue now
          </Button>
        </div>
      }
    >
      {drain.data ? (
        <Notice kind="info">
          {drain.data.sent} sent · {drain.data.failed} failed · {drain.data.held} held.
        </Notice>
      ) : null}
      <Table
        columns={columns}
        rows={messages.data ?? []}
        getKey={(row) => row.id}
        empty={<Empty title="Nothing in the outbox" body={['No email has been queued from this company.']} />}
      />
    </Panel>
  )
}
